import type { WatchSource } from 'vue';
import {
  computed,
  markRaw,
  reactive,
  ref,
  toRaw,
} from 'vue';

import type {
  DrawLayerDeleteDTO,
  DrawLayerFeatureUpdateDTO,
  DrawLayerGeometryPatchDTO,
  DrawLayerResponseDTO,
  GeoJsonFeatureCollectionDTO,
  GeoJsonFeatureDTO,
  OperationDefaultDrawLayerUpdatedEventDTO,
} from '@nora-monorepo/nora-api-client';
import { DrawLayerTypeEnum } from '@nora-monorepo/nora-api-client';
import type { BBox } from '@nora-monorepo/shared';
import { isNonEmptyString } from '@nora-monorepo/shared';
import { downloadTextFile } from '@nora-monorepo/ui-shared';

import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import { OperationBboxStateManager } from '#nora-ui/map/map/state-managers/OperationBboxStateManager';
import type { DrawStateSnapshot, Persistable } from '#nora-ui/map/types/persistence.types';
import { useDrawLayerApiStore } from '#nora-ui/stores/api/draw-layer-api-store';
import { useOperationStore } from '#nora-ui/stores/operation-store';

/**
 * The bbox features fetched for one operation: a FeatureCollection per draw layer id.
 */
type GeoJsonByDrawLayerId = Record<string, GeoJsonFeatureCollectionDTO | undefined>;

export class DrawLayerStateManager extends OperationBboxStateManager<GeoJsonByDrawLayerId> implements Persistable<DrawStateSnapshot> {
  private visibleDrawLayers = reactive(new Map<string, boolean>());
  private api = useDrawLayerApiStore();
  private operationStore = useOperationStore();
  private drawLayerMenuOpenRef = ref(false);

  private drawLayersRef = ref<DrawLayerResponseDTO[]>([]);

  get drawLayerMenuOpen(): boolean {
    return this.drawLayerMenuOpenRef.value;
  }

  /**
   * The user's explicit draw layer choice as persisted on the map config.
   */
  explicitDrawLayerId = computed((): string | undefined => {
    return this.map.ref.activeDrawLayerId;
  });

  /**
   * The effective draw target: the explicit selection when it belongs to the active
   * operation, otherwise the active operation's default draw layer.
   */
  activeDrawLayer = computed((): DrawLayerResponseDTO | undefined => {
    return this.resolveEffectiveDrawLayer();
  });

  activeDrawLayerId = computed((): string | undefined => {
    return this.activeDrawLayer.value?.id;
  });

  activeDrawLayerLabel = computed((): string | undefined => {
    return this.activeDrawLayer.value?.name;
  });

  hasActiveDrawLayer = computed((): boolean => {
    return this.activeDrawLayer.value !== undefined;
  });

  get drawLayers(): DrawLayerResponseDTO[] {
    return this.drawLayersRef.value;
  }

  areAllDrawLayersVisible = computed((): boolean => {
    return this.drawLayersRef.value.every(drawLayer => this.isDrawLayerVisible(drawLayer.id));
  });

  operationLayerLabel = computed((): string => {
    const labelParts: string[] = [];

    if (this.map.ref.activeOperation) {
      labelParts.push(this.map.ref.activeOperation.name);
    }

    if (isNonEmptyString(this.activeDrawLayerLabel.value)) {
      labelParts.push(this.activeDrawLayerLabel.value);
    }

    if (labelParts.length > 0) {
      return labelParts.join(' - ');
    }

    return 'Layers';
  });

  constructor(map: NoraMap) {
    super(map);
  }

  setDrawLayerMenuOpen(isOpen: boolean): void {
    this.drawLayerMenuOpenRef.value = isOpen;
  }

  toggleDrawLayerMenuOpen(): void {
    this.setDrawLayerMenuOpen(!this.drawLayerMenuOpenRef.value);
  }

  drawLayersForOperation(operationId: string): DrawLayerResponseDTO[] {
    return this.drawLayersRef.value.filter(drawLayer => drawLayer.operationId === operationId);
  }

  async loadDrawLayers(): Promise<void> {
    await Promise.all(
      this.map.ref.visibleOperationIds.map(async operationId => await this.fetchOperationDrawLayers(operationId)),
    );
  }

  /**
   * Loads a single operation's draw layers and features alongside the operations already visible
   */
  override async loadOperation(operationId: string): Promise<void> {
    await this.fetchOperationDrawLayers(operationId);
    await super.loadOperation(operationId);
  }

  /**
   * Fetches the features of an operation's visible draw layers for the given bbox. No
   * visible layers is a valid, empty result — it must count as a successful state change
   * so the layer still refreshes (e.g. after the operation's last layer is deleted). Only
   * a failed fetch returns undefined, so the bbox checksum stays stale and the next load
   * retries.
   */
  protected override async fetchOperationData(bbox: BBox, operationId: string): Promise<GeoJsonByDrawLayerId | undefined> {
    const drawLayerIds = this.visibleDrawLayerIdsForOperation(operationId);

    if (drawLayerIds.length === 0) {
      return {};
    }

    const geoJson = await this.api.getBBoxFeatures(bbox, operationId, drawLayerIds);

    if (!geoJson) {
      return undefined;
    }

    return geoJson;
  }

  /**
   * Patches the fetched FeatureCollections into the corresponding draw layer DTOs; the draw
   * layer array itself is the storage, there is no separate per-operation blob.
   */
  protected override storeOperationData(operationId: string, geoJsonByDrawLayerId: GeoJsonByDrawLayerId): void {
    for (const drawLayer of this.drawLayersForOperation(operationId)) {
      if (!this.isDrawLayerVisible(drawLayer.id)) {
        continue;
      }
      const featureCollection = geoJsonByDrawLayerId[drawLayer.id];

      // markRaw: the features are only read to hand to OpenLayers, so keep them out of Vue's
      // reactive graph — wrapping thousands of them per fetch was a big cost.
      drawLayer.geoJson = featureCollection ? markRaw(featureCollection) : undefined;
    }
  }

  /**
   * Removes a released operation's draw layers (including the user's drafts in it) and clears
   * their visibility/active state.
   */
  protected override removeOperationData(operationId: string): boolean {
    const removedLayers = this.drawLayersForOperation(operationId);

    this.drawLayersRef.value = this.drawLayersRef.value.filter(
      drawLayer => drawLayer.operationId !== operationId,
    );

    for (const drawLayer of removedLayers) {
      this.visibleDrawLayers.delete(drawLayer.id);

      if (this.map.ref.activeDrawLayerId === drawLayer.id) {
        this.clearActiveDrawLayer();
      }
    }

    return removedLayers.length > 0;
  }

  protected override async refreshLayer(): Promise<void> {
    await this.map.layers.draw.load();
  }

  /**
   * A fetch is determined by the bbox AND which draw layers are visible, so toggling a
   * layer's visibility triggers a refetch even for an unchanged bbox.
   */
  protected override getChecksumForBbox(bbox: BBox, operationId: string): string {
    const drawLayerIds = this.visibleDrawLayerIdsForOperation(operationId);

    return `${bbox.getAsBBoxString()}|${drawLayerIds.sort().join(',')}`;
  }

  /**
   * Load/reload draw layer state from the API.
   * Clears existing state before loading.
   */
  async load(): Promise<void> {
    this.unload();
    await this.loadDrawLayers();

    // We can make this a fire-and-forget since the draw layer features will load on pan when online, and we don't want to block on this for the rest of the map to load
    this.loadCurrentBbox().catch((error) => {
      console.error('Error loading draw layer features for bbox:', error);
    });
  }

  async loadDrawlayerFeaturesFromState(): Promise<void> {
    await this.map.layers.draw.load();
  }

  /**
   * The ids of the visible draw layers belonging to a given operation.
   */
  private visibleDrawLayerIdsForOperation(operationId: string): string[] {
    return this.drawLayersForOperation(operationId)
      .map(drawLayer => drawLayer.id)
      .filter(id => this.isDrawLayerVisible(id));
  }

  /**
   * Unload/clear all draw layer state. The explicit draw layer choice on the map config
   * is left alone: it is harmless while its layer is unloaded and resolves again on load.
   */
  unload(): void {
    this.drawLayersRef.value = [];
    this.visibleDrawLayers.clear();
    this.invalidateBboxChecksums();
  }

  /**
   * Adds a draw layer to the map and reloads its features. Layers of operations not
   * visible on this map are ignored.
   */
  async addDrawLayer(drawLayer: DrawLayerResponseDTO): Promise<void> {
    if (!this.map.ref.isOperationVisible(drawLayer.operationId)) {
      return;
    }

    this.upsertDrawLayer(drawLayer);

    await this.invalidateBboxAndReloadFeatures();
  }

  /**
   * Upserts the created draw layer if its operation is visible on this map, or removes a
   * stale local copy otherwise. Move and publish are also broadcast as create events.
   */
  async handleDrawLayerCreateEvent(data: DrawLayerResponseDTO): Promise<void> {
    if (this.map.ref.isOperationVisible(data.operationId)) {
      this.upsertDrawLayer(data);
    }
    else {
      this.removeDrawLayerFromState(data.id);
    }

    await this.invalidateBboxAndReloadFeatures();
  }

  async handleDrawLayerUpdateEvent(data: DrawLayerResponseDTO): Promise<void> {
    const index = this.drawLayersRef.value.findIndex(
      drawLayer => drawLayer.id === data.id,
    );

    if (index !== -1) {
      this.drawLayersRef.value[index] = data;
    }
  }

  async handleDrawLayerDeleteEvent(data: DrawLayerDeleteDTO): Promise<void> {
    if (!this.getDrawLayerById(data.id)) {
      return;
    }

    this.removeDrawLayerFromState(data.id);

    await this.invalidateBboxAndReloadFeatures();
  }

  async handleDrawLayerFeatureUpdateEvent(_data: DrawLayerFeatureUpdateDTO): Promise<void> {
    await this.invalidateBboxAndReloadFeatures();
  }

  async handleOperationDefaultDrawLayerUpdatedEvent(data: OperationDefaultDrawLayerUpdatedEventDTO): Promise<void> {
    for (const drawLayer of this.drawLayersForOperation(data.operationId)) {
      drawLayer.isDefault = drawLayer.id === data.newDrawLayerId;
    }
  }

  setActiveDrawLayer(drawLayerId: string | undefined): void {
    this.map.ref.setActiveDrawLayer(drawLayerId);

    if (isNonEmptyString(drawLayerId)) {
      this.setDrawLayerVisibility(drawLayerId, true);
    }
  }

  /**
   * Clears the explicit draw layer choice; the effective target then falls back to the
   * active operation's default draw layer via `activeDrawLayer`.
   */
  clearActiveDrawLayer(): void {
    this.map.ref.clearActiveDrawLayer();
  }

  async toggleDrawLayerLock(drawLayerId: string): Promise<void> {
    const drawLayer = this.getDrawLayerByIdOrThrow(drawLayerId);

    if (drawLayer.locked) {
      await this.unlockDrawLayer(drawLayerId);
    }
    else {
      await this.lockDrawLayer(drawLayerId);
    }
  }

  async unlockDrawLayer(drawLayerId: string): Promise<void> {
    const drawLayer = this.getDrawLayerById(drawLayerId);

    if (drawLayer && drawLayer.locked) {
      await this.api.unlockOperationDrawLayer(drawLayerId, drawLayer.operationId);

      drawLayer.locked = false;
    }
  }

  async lockDrawLayer(drawLayerId: string): Promise<void> {
    const drawLayer = this.getDrawLayerById(drawLayerId);

    if (drawLayer && !drawLayer.locked) {
      await this.api.lockOperationDrawLayer(drawLayerId, drawLayer.operationId);

      drawLayer.locked = true;
    }
  }

  async setDefaultDrawLayer(drawLayerId: string): Promise<boolean> {
    const drawLayer = this.getDrawLayerByIdOrThrow(drawLayerId);
    const success = await this.api.setDefaultDrawLayer(drawLayerId, drawLayer.operationId);

    if (success) {
      for (const operationDrawLayer of this.drawLayersForOperation(drawLayer.operationId)) {
        operationDrawLayer.isDefault = operationDrawLayer.id === drawLayerId;
      }
    }

    return success;
  }

  isDraftDrawLayer(drawLayerId: string): boolean {
    return this.getDrawLayerById(drawLayerId)?.draft === true;
  }

  reorderDrawLayers(draggedLayerId: string, targetLayerId: string): void {
    const layers = [...this.drawLayersRef.value];
    const draggedIndex = layers.findIndex(layer => layer.id === draggedLayerId);
    const targetIndex = layers.findIndex(layer => layer.id === targetLayerId);

    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    const [draggedItem] = layers.splice(draggedIndex, 1);
    layers.splice(targetIndex, 0, draggedItem);

    this.drawLayersRef.value = layers;

    this.map.layers.draw.recalculateOperationDrawLayerZIndex();
  }

  async exportDrawLayerById(drawLayerId: string): Promise<void> {
    const drawLayer = this.getDrawLayerByIdOrThrow(drawLayerId);
    const geoJson = await this.api.getOperationDrawLayerGeoJsonById(drawLayerId, drawLayer.operationId);

    if (geoJson) {
      this.downloadGeoJsonAsFile(geoJson, drawLayer.name);
    }
  }

  async updateDrawLayerGeometry(
    patch: DrawLayerGeometryPatchDTO,
    drawLayerId: string,
  ): Promise<void> {
    const drawLayer = this.getDrawLayerByIdOrThrow(drawLayerId);

    await this.api.updateOperationDrawLayerGeometry(
      drawLayerId,
      drawLayer.operationId,
      patch,
    );

    this.applyGeometryPatchLocally(drawLayer, patch);
  }

  /**
   * Folds a geometry patch into the draw layer's own features.
   *
   * Offline there is no SSE echo, so without this a shape drawn offline is missing from the
   * persisted snapshot and gone after a restart.
   *
   * This runs even when the write failed: `updateOperationDrawLayerGeometry` logs and swallows the
   * error, so in the web app a rejected patch stays visible until the next refetch corrects it.
   * Webview mutations always resolve, so the offline path never hits that case.
   */
  private applyGeometryPatchLocally(
    drawLayer: DrawLayerResponseDTO,
    patch: DrawLayerGeometryPatchDTO,
  ): void {
    // Replaced, not mutated: `geoJson` is markRaw'd, so the persistence watcher only sees the
    // property assignment.
    drawLayer.geoJson = markRaw({
      type: 'FeatureCollection',
      features: this.foldGeometryPatch(drawLayer.geoJson?.features ?? [], patch),
    });
  }

  /**
   * Folds a geometry patch into a feature list, keyed by feature id so applying the same patch
   * twice is a no-op.
   * @param existing - the features currently on the draw layer
   * @param patch - the add/update/remove to apply
   * @returns the resulting feature list
   */
  private foldGeometryPatch(
    existing: GeoJsonFeatureDTO[],
    patch: DrawLayerGeometryPatchDTO,
  ): GeoJsonFeatureDTO[] {
    const featuresById = new Map<string, GeoJsonFeatureDTO>(
      existing.map(feature => [feature.id, feature]),
    );

    for (const featureId of patch.remove ?? []) {
      featuresById.delete(featureId);
    }

    for (const feature of [...(patch.add ?? []), ...(patch.update ?? [])]) {
      featuresById.set(feature.id, feature);
    }

    return [...featuresById.values()];
  }

  async deleteDrawLayerById(drawLayerId: string): Promise<void> {
    const drawLayer = this.getDrawLayerByIdOrThrow(drawLayerId);

    await this.api.deleteOperationDrawLayer(drawLayerId, drawLayer.operationId);

    this.removeDrawLayerFromState(drawLayer.id);

    await this.invalidateBboxAndReloadFeatures();
  }

  /**
   * Publish a draft draw layer, making it visible to all operation members.
   */
  async publishDrawLayer(drawLayerId: string): Promise<void> {
    const drawLayer = this.getDrawLayerByIdOrThrow(drawLayerId);

    const publishedDrawLayer = await this.api.publishDrawLayer(drawLayerId, drawLayer.operationId);

    if (publishedDrawLayer) {
      drawLayer.draft = false;
    }
  }

  getDrawLayerById(drawLayerId: string): DrawLayerResponseDTO | undefined {
    return this.drawLayersRef.value.find(drawLayer => drawLayer.id === drawLayerId);
  }

  getDrawLayerByIdOrThrow(drawLayerId: string): DrawLayerResponseDTO {
    const drawLayer = this.getDrawLayerById(drawLayerId);

    if (!drawLayer) {
      throw new Error(`Draw layer with id ${drawLayerId} not found`);
    }

    return drawLayer;
  }

  setDrawLayerVisibility(drawLayerId: string, isVisible: boolean): void {
    this.visibleDrawLayers.set(drawLayerId, isVisible);

    this.map.layers.draw.setDrawLayerVisibility(drawLayerId, isVisible);
  }

  isDrawLayerVisible(drawLayerId: string): boolean {
    return this.visibleDrawLayers.get(drawLayerId) ?? true;
  }

  hideDrawLayer(drawLayerId: string): void {
    this.setDrawLayerVisibility(drawLayerId, false);
  }

  showDrawLayer(drawLayerId: string): void {
    this.setDrawLayerVisibility(drawLayerId, true);
  }

  toggleDrawLayerVisibility(drawLayerId: string): void {
    const currentState = this.isDrawLayerVisible(drawLayerId);
    this.setDrawLayerVisibility(drawLayerId, !currentState);
  }

  toggleAllDrawLayersVisibility(): void {
    if (this.areAllDrawLayersVisible.value) {
      for (const drawLayer of this.drawLayersRef.value) {
        this.hideDrawLayer(drawLayer.id);
      }
    }
    else {
      for (const drawLayer of this.drawLayersRef.value) {
        this.showDrawLayer(drawLayer.id);
      }
    }
  }

  clearAllVisibility(): void {
    this.visibleDrawLayers.clear();
  }

  async invalidateBboxAndReloadFeatures(): Promise<void> {
    this.invalidateBboxChecksums();
    await this.loadCurrentBbox();
  }

  private upsertDrawLayer(drawLayer: DrawLayerResponseDTO): void {
    const index = this.drawLayersRef.value.findIndex(
      existingDrawLayer => existingDrawLayer.id === drawLayer.id,
    );

    if (index === -1) {
      this.drawLayersRef.value.push(drawLayer);
    }
    else {
      this.drawLayersRef.value[index] = drawLayer;
    }
  }

  private removeDrawLayerFromState(drawLayerId: string): void {
    this.drawLayersRef.value = this.drawLayersRef.value.filter(
      drawLayer => drawLayer.id !== drawLayerId,
    );

    this.visibleDrawLayers.delete(drawLayerId);

    if (this.map.ref.activeDrawLayerId === drawLayerId) {
      this.clearActiveDrawLayer();
    }
  }

  private downloadGeoJsonAsFile(geoJson: object, fileName: string): void {
    downloadTextFile(
      JSON.stringify(geoJson, null, 2),
      `${fileName}.geojson`,
    );
  }

  private async fetchOperationDrawLayers(operationId: string): Promise<void> {
    const operationDrawLayers = await this.operationStore.fetchOperationDrawLayers(operationId);

    // Detach any attached feature graph from Vue reactivity (see storeOperationData).
    for (const drawLayer of operationDrawLayers) {
      if (drawLayer.geoJson) {
        drawLayer.geoJson = markRaw(drawLayer.geoJson);
      }
    }

    // Merge: replace only this operation's layers, leaving other visible operations intact.
    this.drawLayersRef.value = [
      ...this.drawLayersRef.value.filter(drawLayer => drawLayer.operationId !== operationId),
      ...operationDrawLayers,
    ];
  }

  /**
   * Resolve the effective active draw layer, this takes into account:
   * - Whether the user has selected a draw layer manually (explicitDrawLayerId)
   * - Which operations are visible and whether those operations have a default draw layer
   */
  private resolveEffectiveDrawLayer(): DrawLayerResponseDTO | undefined {
    const activeOperationId = this.map.ref.activeOperationId;

    if (activeOperationId === undefined) {
      return undefined;
    }

    const explicitDrawLayerId = this.explicitDrawLayerId.value;

    if (isNonEmptyString(explicitDrawLayerId)) {
      const explicitDrawLayer = this.getDrawLayerById(explicitDrawLayerId);

      if (explicitDrawLayer && explicitDrawLayer.operationId === activeOperationId) {
        return explicitDrawLayer;
      }
    }

    return this.drawLayersForOperation(activeOperationId).find(
      drawLayer => drawLayer.isDefault
        && drawLayer.type !== DrawLayerTypeEnum.GEOFENCE
        && !drawLayer.draft,
    );
  }

  /**
   * Serialize the draw layer state to a plain object for persistence.
   */
  serialize(): DrawStateSnapshot {
    return {
      drawLayers: toRaw(this.drawLayersRef.value),
      visibility: Array.from(this.visibleDrawLayers.entries()),
    };
  }

  /**
   * Restore draw layer state from a persisted snapshot.
   */
  deserialize(snapshot: DrawStateSnapshot): void {
    // Detach persisted feature graphs from Vue reactivity before they enter the ref.
    for (const drawLayer of snapshot.drawLayers) {
      if (drawLayer.geoJson) {
        drawLayer.geoJson = markRaw(drawLayer.geoJson);
      }
    }

    this.drawLayersRef.value = snapshot.drawLayers;

    this.visibleDrawLayers.clear();

    for (const [id, visible] of snapshot.visibility) {
      this.visibleDrawLayers.set(id, visible);
    }
  }

  /**
   * Returns a watch source for triggering persistence when state changes.
   */
  getWatchSource(): WatchSource {
    return () => [
      this.drawLayersRef.value,
      Array.from(this.visibleDrawLayers.entries()),
    ];
  }
}
